import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { FeedbackReport, FeedbackDisLike, FeedbackLike } from 'Containers/Feedback';
import { ForwardButton, BackwardButton } from 'Components/VideoPlayer/components';
import { useConfig } from 'Context/ConfigContext';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { navigateToTungKai } from 'Util/navigateToTungKai';
import { myProfile } from 'Redux/user-profile/selectors';
import { canRedirectToTungKai } from 'Routes/MyCourses/Components/utils.js';
import { EXAM_MODE, QUIZ_VARIANT } from 'Util/constants';

import { Box, Flex, SvgIcon } from '../../../Components/Base';
import { withCapacitor } from '../../../Util/Capacitors/hoc';
import { ClassroomDrawer } from '../../../Components/Classroom/Components';
import { VjsComponent } from '../../../Components/VideoPlayer';
import { withVideoPlayer } from '../../../Context/VideoPlayer';
import { IS_SHOW_VIDEO_RESOLUTION } from '../../../Util/Capacitors/constant';
import { useClassroomContext, LAYOUT_TYPE, LEARNING_MODE } from '../../../Context/ClassroomContext';
import CourseToCPanel from '../../../Components/Classroom/CourseToCPanel/CourseToCPanel';
import NextSubsectionPanel from '../../../Components/Classroom/NextSubsectionPanel';
import DownloadableContentView from '../../../Components/Classroom/DownloadableContent';
import CourseDocumentPanelView from '../../../Components/Classroom/CourseDocumentPanel';
import { ClassroomNavigation, ClassroomSubsection } from '../../../Domain/Classroom';
import { SubsectionContent } from '../../Classroom/Components/';
import { MATERIAL_TYPE } from '../../../Components/Classroom/Content';
import { useClassroomTracking } from '../../../Util/Analytics';
import { useRestorePlayerFocus } from '../../../Util/Hooks';
import ClearByOnDemand from '../Featured/ClearByOnDemand/Container/ClearByOnDemand';
import ClearByLearnEd from '../Featured/ClearByLearnEd/Container/ClearByLearnEd';
import OnlineSolutionButton from '../Featured/ClearByOnDemand/Container/ClearPlus/OnlineSolutionButton';
import ClearFAQButton from '../Featured/ClearByOnDemand/Container/ClearPlus/ClearFAQButton';

import IconComponent from './IconComponent';

const CenterWrapper = styled(Flex)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  height: 52px;
`;

const FeedbackWrapper = ({ layoutType, ...props }) => {
  let display = ['none', 'flex'];
  if (layoutType === LAYOUT_TYPE.QUIZ || layoutType === LAYOUT_TYPE.EBOOK) {
    display = 'none';
  } else if (layoutType === LAYOUT_TYPE.EXTERNAL_VIDEO_PLAYER) {
    display = 'flex';
  }

  return (
    <Box display={display} width={'46px'} justifyContent="center" alignItems="center" {...props} />
  );
};

const ResponsiveBox = styled(Box)`
  display: block;

  ${IS_SHOW_VIDEO_RESOLUTION &&
  css`
    @media (max-width: ${({ theme }) => theme.breakpoints[0]}) {
      display: none;
    }
  `};
`;

const ClearWrapper = ({ layoutType, ...props }) => {
  let display = { _: 'none', sm: 'flex' };
  if (layoutType === LAYOUT_TYPE.QUIZ) {
    display = 'none';
  } else if (layoutType === LAYOUT_TYPE.EBOOK || layoutType === LAYOUT_TYPE.EXTERNAL_VIDEO_PLAYER) {
    display = 'flex';
  }
  return <Box display={display} {...props} />;
};

const Footer = ({
  isDrawerOpen,
  onToggleDrawer,
  onExamClick,
  horizontalDrawer,
  videoPlayerContext,
  isClassroomActive,
  t,
  history,
  capacitorContext,
  name,
  user,
  ...restProps
}) => {
  const { player } = videoPlayerContext || {};
  const { classroom, tungKai, userApi } = useConfig();
  const restorePlayerFocus = useRestorePlayerFocus();
  const handleDrawerClose = () => {
    onToggleDrawer(false);
    restorePlayerFocus();
  };
  const [isSubsectionEnd, setIsSubsectionEnd] = useState(false);
  const [isShowNextSubection, setIsShowNextSubsection] = useState(false);
  const handleExitClassroom = () => {
    history.push('/');
  };

  const {
    logTocToggleClick,
    logDocumentToggleClick,
    logDocumentTabClick,
    logSectionGroupClick,
    logSectionClick,
    logSubsectionClick,
    logDownloadItemClick,
    logSubsectionContentClick,
  } = useClassroomTracking();
  const {
    updateLayout,
    layoutType,
    currentLearningMode,
    setCurrentLearningMode,
    courseData,
    courseId,
    currentSubsectionId,
    updateCurrentSubsection,
  } = useClassroomContext();
  const { isMobile } = useCompatibleDisplay();

  const isQuizLayout = layoutType === LAYOUT_TYPE.QUIZ;
  const { requestAppReview, openInApp } = capacitorContext;
  const { courseLearningModeDetails, subsections: defaultSubsections = [] } = courseData;

  const enabledLearningMode = classroom?.enableLearningMode;
  const isLearnCraftCourse = courseData?.courseSourceType === 'LEARNCRAFT';
  const isLayoutEbookType = layoutType === LAYOUT_TYPE.EBOOK;
  const isExternalVideoPlayerLayout = layoutType === LAYOUT_TYPE.EXTERNAL_VIDEO_PLAYER;
  const isLearnCraftNewLayout = isLearnCraftCourse && isExternalVideoPlayerLayout;
  const userId = user?.id;

  useEffect(() => {
    if (player) {
      player.on('ended', () => {
        setIsSubsectionEnd(true);
        updateLayout(LAYOUT_TYPE.DEFAULT_STICKY);
        requestAppReview();
      });
      player.on('play', () => {
        setIsSubsectionEnd(false);
        updateLayout(LAYOUT_TYPE.DEFAULT);
      });
    }
  }, [player]);

  useEffect(() => {
    if (isSubsectionEnd && !isQuizLayout) {
      setIsShowNextSubsection(true);
    } else {
      setIsShowNextSubsection(false);
    }
  }, [isSubsectionEnd, isQuizLayout]);

  const isCurrentLearningModeActive = useMemo(
    () =>
      courseLearningModeDetails.find(
        (learningModeDetail) => learningModeDetail.id === currentLearningMode
      )?.isActive,
    [courseLearningModeDetails, currentLearningMode]
  );

  useEffect(() => {
    const currentSubsectionDefaultIndex = defaultSubsections.findIndex(
      ({ id }) => id === currentSubsectionId
    );

    if (!isCurrentLearningModeActive && currentSubsectionDefaultIndex >= 0) {
      const subsectionDefault = defaultSubsections[currentSubsectionDefaultIndex];
      setCurrentLearningMode(LEARNING_MODE.DEFAULT);
      updateCurrentSubsection(subsectionDefault.id, subsectionDefault.referId);
    }
  }, [
    isCurrentLearningModeActive,
    currentSubsectionId,
    defaultSubsections,
    setCurrentLearningMode,
    updateCurrentSubsection,
  ]);

  return (
    <Box {...restProps}>
      {isShowNextSubection && (
        <ClassroomNavigation>
          {({ nextSubsection, goToNextSubsection }) => {
            return nextSubsection ? (
              <NextSubsectionPanel
                subsectionName={nextSubsection && nextSubsection.title}
                onNext={goToNextSubsection}
                color={'white'}
              />
            ) : null;
          }}
        </ClassroomNavigation>
      )}

      <Box>
        <Flex width={'100%'} justifyContent={'space-between'} alignItems={'center'}>
          <VjsComponent
            name={`${name}-current-time-display`}
            player={player}
            componentType={VjsComponent.COMPONENT_TYPES.CURERNT_TIME_DISPLAY}
            minWidth="auto"
          />
          <VjsComponent
            name={`${name}-progress-control`}
            player={player}
            componentType={VjsComponent.COMPONENT_TYPES.PROGRESS_CONTROL}
            width={'100%'}
          />
          <VjsComponent
            name={`${name}-duration-display`}
            player={player}
            componentType={VjsComponent.COMPONENT_TYPES.DURATION_DISPLAY}
            minWidth="auto"
          />
        </Flex>
        {isLearnCraftNewLayout && isMobile && (
          <Flex width={'100%'} justifyContent={'space-between'} alignItems={'center'} mb={6} px={4}>
            <OnlineSolutionButton />
            <ClearFAQButton />
          </Flex>
        )}
        <Flex
          justifyContent={'space-between'}
          alignItems={'center'}
          width={{ _: isLearnCraftNewLayout ? '100%' : 'auto', sm: 'auto' }}
          style={{ position: 'relative' }}
          mb={2}
          pl={2}
          pr={{ _: 3, sm: 4 }}
        >
          <ClassroomNavigation>
            {({
              isSectionGroupActive = false,
              sectionGroups = [],
              sections = [],
              goToSubsection,
              currentSubsection,
            }) => {
              const mapSections = sections.map(({ subSections, ...section }) => ({
                subsections: subSections.map(({ number, ...subSection }) => ({
                  subsectionIndex: number,
                  ...subSection,
                })),
                ...section,
              }));

              const mapSectionGroups = sectionGroups.map(({ sections, ...sectionGroup }) => {
                return {
                  sections: sections.map(({ subsections, ...section }) => ({
                    subsections: subsections.map((subSection, index) => {
                      return {
                        subsectionIndex: index,
                        ...subSection,
                      };
                    }),
                    ...section,
                  })),
                  ...sectionGroup,
                };
              });

              return (
                <Flex
                  width={{ _: isLearnCraftNewLayout ? '100%' : 'auto', sm: 'auto' }}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <CourseToCPanel
                    courseLearningModeDetails={courseLearningModeDetails}
                    isSectionGroupActive={isSectionGroupActive}
                    sectionGroups={mapSectionGroups}
                    sections={mapSections}
                    onSectionGroupClick={logSectionGroupClick}
                    onSectionClick={logSectionClick}
                    onSubsectionClick={({ subsection, section }) => {
                      logSubsectionClick(subsection?.title);
                      logSubsectionContentClick({
                        subsectionTitle: subsection?.title,
                        subsectionContentType: subsection?.contentType,
                      });

                      const examMode = subsection?.metadata?.examMode;
                      const { enable, enableWhiteList, whitelist, quizFooterRedirectPath } =
                        tungKai || {};

                      const canGoToTungKai = canRedirectToTungKai({
                        enable,
                        enableWhiteList,
                        whitelist,
                        userId,
                      });
                      const isQuizFeed = subsection?.contentType === 'QUIZ_FEED';

                      if ((examMode === EXAM_MODE.RANDOM || isQuizFeed) && canGoToTungKai) {
                        const variant = isQuizFeed ? QUIZ_VARIANT.QUIZ_FEED : QUIZ_VARIANT.QUIZ_V2;
                        const redirectPath = (quizFooterRedirectPath || '/')
                          .replace('{courseId}', courseId)
                          .replace('{subsectionId}', subsection?.id)
                          .replace('{variant}', variant);

                        return navigateToTungKai({
                          redirectPath,
                          config: { userApi, tungKai },
                          openInApp,
                        });
                      }

                      // default behavior (non-random)
                      goToSubsection(subsection?.id, subsection?.referId);
                    }}
                    title={t('COURSE_TOC_PANEL.TITLE')}
                    toggleComponent={
                      <IconComponent
                        data-testid={`btn-${name}-sidebar-list-toggle`}
                        iconComponent={
                          <SvgIcon icon={SvgIcon.ICON_LIST.LIST} color={'white'} size={24} />
                        }
                      />
                    }
                    onToggleComponentClick={logTocToggleClick}
                    isOpen={isDrawerOpen}
                    drawerProps={{
                      onDrawerOpen: () => onToggleDrawer(true),
                      onDrawerClose: handleDrawerClose,
                      horizontalAnchor: ClassroomDrawer.ANCHOR_TYPES.LEFT,
                      horizontal: horizontalDrawer,
                    }}
                    enabledLearningMode={enabledLearningMode}
                    currentLearningMode={currentLearningMode}
                    setCurrentLearningMode={setCurrentLearningMode}
                    onClickOnlineSolutionButton={handleDrawerClose}
                    isLearnCraftCourse={isLearnCraftCourse}
                  />
                  {currentSubsection && !isLayoutEbookType && (
                    <CourseDocumentPanelView
                      downloadTabComponent={
                        <ClassroomSubsection>
                          {({ materials, getDownloadlink }) => {
                            const handleDownloadMaterial = async (fieldKey, assetReferId) => {
                              const { openUrl } = capacitorContext;
                              const downloadUrl = await getDownloadlink(fieldKey, assetReferId);
                              logDownloadItemClick(
                                fieldKey.substring(fieldKey.lastIndexOf('/') + 1)
                              );
                              const downloadMaterial = (downloadUrl) => {
                                const downloadElement = document.createElement('a');
                                downloadElement.setAttribute('href', downloadUrl);
                                downloadElement.setAttribute('target', '_blank');
                                downloadElement.click();
                                downloadElement.remove();
                              };
                              if (typeof openUrl === 'function') {
                                openUrl(downloadUrl, () => downloadMaterial(downloadUrl));
                              } else {
                                downloadMaterial(downloadUrl);
                              }
                            };
                            return (
                              <DownloadableContentView
                                relevantMaterials={materials}
                                onDownloadClick={handleDownloadMaterial}
                              />
                            );
                          }}
                        </ClassroomSubsection>
                      }
                      articleTabComponent={
                        <SubsectionContent
                          onExitClassroom={handleExitClassroom}
                          allowedTypes={[MATERIAL_TYPE.ARTICLE]}
                        />
                      }
                      toggleComponent={
                        <IconComponent
                          data-testid={`btn-${name}-attachment-toggle`}
                          iconComponent={
                            <SvgIcon
                              icon={SvgIcon.ICON_LIST.ATTACHMENT}
                              color={'white'}
                              size={24}
                              style={{ transform: 'rotate(-45deg)' }}
                            />
                          }
                        />
                      }
                      onToggleComponentClick={logDocumentToggleClick}
                      onTabChange={(tabTitle) => logDocumentTabClick(tabTitle)}
                      drawerProps={{
                        onDrawerOpen: () => onToggleDrawer(true),
                        onDrawerClose: handleDrawerClose,
                        horizontalAnchor: ClassroomDrawer.ANCHOR_TYPES.LEFT,
                        horizontal: horizontalDrawer,
                      }}
                    />
                  )}
                  <FeedbackWrapper
                    data-testid={`btn-${name}-feedback-like-toggle`}
                    layoutType={layoutType}
                  >
                    <FeedbackLike />
                  </FeedbackWrapper>
                  <FeedbackWrapper
                    data-testid={`btn-${name}-feedback-dislike-toggle`}
                    layoutType={layoutType}
                  >
                    <FeedbackDisLike />
                  </FeedbackWrapper>
                  <FeedbackWrapper
                    data-testid={`btn-${name}-feedback-report-toggle`}
                    layoutType={layoutType}
                  >
                    <FeedbackReport />
                  </FeedbackWrapper>
                </Flex>
              );
            }}
          </ClassroomNavigation>
          {/* {IS_SHOW_VIDEO_RESOLUTION && (
            <ResponsiveBox width={'46px'} style={{ minWidth: '46px', height: '46px' }} />
          )} */}
          <CenterWrapper>
            <BackwardButton name={`${name}-backward`} player={player} />
            <VjsComponent
              name={`${name}-play-pause`}
              player={player}
              componentType={VjsComponent.COMPONENT_TYPES.PLAY_TOGGLE}
            />
            <ForwardButton name={`${name}-forward`} player={player} />
          </CenterWrapper>
          <Flex height={'52px'} alignItems={'center'}>
            {isLearnCraftCourse ? (
              ((!isMobile && !isQuizLayout) || (isMobile && isLayoutEbookType)) && (
                <Flex>
                  <OnlineSolutionButton />
                  <Box mr={4} />
                  <ClearFAQButton />
                </Flex>
              )
            ) : (
              <ClearWrapper layoutType={layoutType}>
                <ClearByOnDemand />
              </ClearWrapper>
            )}
            <ClearWrapper layoutType={layoutType}>
              <ClearByLearnEd />
            </ClearWrapper>
            <Flex alignItems={'center'} ml={player ? 7 : 0}>
              {IS_SHOW_VIDEO_RESOLUTION && (
                <VjsComponent
                  name={`${name}-settings`}
                  player={player}
                  componentType={VjsComponent.COMPONENT_TYPES.QUALITY_SELECTOR_BUTTON}
                >
                  <Flex
                    justifyContent={'center'}
                    alignItems={'center'}
                    pb={2}
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      width: '46px',
                      height: '46px',
                    }}
                  >
                    <SvgIcon icon={SvgIcon.ICON_LIST.SETTINGS} color={'white'} size={24} />
                  </Flex>
                </VjsComponent>
              )}
              <VjsComponent
                name={`${name}-playback-rate`}
                player={player}
                componentType={VjsComponent.COMPONENT_TYPES.PLAYBACK_RATE_MENU_BUTTON}
                options={{ playbackRates: [0.75, 1.0, 1.25, 1.5, 1.75, 2.0] }}
              />
              <ResponsiveBox>
                <VjsComponent
                  name={`${name}-volume-panel`}
                  player={player}
                  componentType={VjsComponent.COMPONENT_TYPES.VOLUME_PANEL}
                  options={{ inline: false }}
                />
              </ResponsiveBox>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

Footer.propTypes = {
  isDrawerOpen: PropTypes.bool,
  onExitClassroom: PropTypes.func,
  onToggleDrawer: PropTypes.func,
  onExamClick: PropTypes.func,
  name: PropTypes.string,
};

Footer.defaultProps = {
  onToggleDrawer: () => {},
};

export default compose(
  withCapacitor,
  withTranslation('classroom'),
  withVideoPlayer,
  connect((state) => ({
    user: myProfile(state) ?? {},
  }))
)(Footer);
